import { Button, message, Upload } from "antd";
import React from "react";
import axios, { AxiosError } from "axios";
import { ENDPOINTS } from "../constants/endpoints.constant";
import { IconUpload } from "@tabler/icons-react";

type UploadRequestOption = {
  data?: Record<string, any>;
  file: File & { is_visible?: boolean };
  filename?: string;
  onError: (error: Error) => void;
  onProgress: (event: { percent: number | string }, file: File) => void;
  onSuccess: (response: any, file: File) => void;
};

const UploadUtils: Record<string, any> = {};

const upload = ({
  data,
  file,

  onError,
  onProgress,
  onSuccess,
}: UploadRequestOption): void => {
  const formData = new FormData();
  if (data) {
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });
  }

  formData.append("attachment[0]", file);

  axios
    .post(
      `${import.meta.env.VITE_APP_API_URL}${ENDPOINTS.attachment}`,
      formData,
      {
        withCredentials: true,
        onUploadProgress: ({ total, loaded }) => {
          if (total) {
            const percent = ((loaded / total) * 100).toFixed(2);
            onProgress({ percent }, file);
          }
        },
      }
    )
    .then((response) => {
      onSuccess(response.data?.data, file);
    })
    .catch((error: AxiosError<any>) => {
      message.error(error?.response?.data?.message || "Upload failed");
      onError(error);
    });
};

// Upload with is_visible = false
const uploadNonVisible = ({
  data,
  file,

  onError,
  onProgress,
  onSuccess,
}: UploadRequestOption): void => {
  const formData = new FormData();
  if (data) {
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });
  }

  file.is_visible = false;
  formData.append("attachment[0]", file);
  formData.append("is_visible", "false");

  axios
    .post(
      `${import.meta.env.VITE_APP_API_URL}${ENDPOINTS.attachment}`,
      formData,
      {
        withCredentials: true,
        onUploadProgress: ({ total, loaded }) => {
          if (total) {
            const percent = ((loaded / total) * 100).toFixed(2);
            onProgress({ percent }, file);
          }
        },
      }
    )
    .then((response) => {
      onSuccess(response.data?.data, file);
    })
    .catch((error: AxiosError<any>) => {
      message.error(error?.response?.data?.message || "Upload failed");
      onError(error);
    });
};

// Upload placeholder content for Dragger
class DraggerContent extends React.Component {
  render(): React.JSX.Element {
    return (
      <div style={{ padding: 30 }}>
        <div>
          <i className='ri-upload-cloud-2-fill f-s-24 text-muted'></i>
        </div>
        <div>
          <strong className='text-primary'>Upload an Image</strong>
          <div className='text-muted'>or drag and drop JPEG or PNG</div>
        </div>
      </div>
    );
  }
}

// Upload content for card buttons
UploadUtils.CardContent = function ({ padding = 30 }: { padding?: number }) {
  return (
    <div style={{ padding }}>
      <div>
        <i className='ri-upload-cloud-2-fill f-s-24 text-muted'></i>
      </div>
      <div>
        <strong className='text-primary'>Upload</strong>
      </div>
    </div>
  );
};

UploadUtils.buttonContent = class extends React.Component {
  render(): React.JSX.Element {
    return (
      <Button
        className='d-flex align-items-center text-muted'
        icon={<IconUpload size={16} />}
      >
        <span>Upload</span>
      </Button>
    );
  }
};

// Common Upload Ant props
UploadUtils.Content = DraggerContent;

UploadUtils.props = {
  action: "",
  listType: "picture-card" as const,
  showUploadList: { showPreviewIcon: false },
  customRequest: upload,
  accept: "image/jpeg, image/png",
  maxCount: 1,
  beforeUpload: (file: File) => {
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("File must be smaller than 5MB!");
      return Upload.LIST_IGNORE; // Prevent upload
    }
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
    }
    return isImage || Upload.LIST_IGNORE;
  },
};

UploadUtils.propsNonVisible = {
  ...UploadUtils.props,
  customRequest: uploadNonVisible,
};

UploadUtils.multipleProps = {
  ...UploadUtils.props,
  multiple: true,
  maxCount: 10,
};

UploadUtils.multiplePropsNonVisible = {
  ...UploadUtils.props,
  customRequest: uploadNonVisible,
  multiple: true,
  maxCount: 3,
};

UploadUtils.multiplePropsNoLimitNonVisible = {
  ...UploadUtils.props,
  customRequest: uploadNonVisible,
  multiple: true,
};

UploadUtils.buttonTypeProps = {
  action: "",
  customRequest: uploadNonVisible,
  accept: "image/jpeg, image/png",
  maxCount: 1,
  showUploadList: false,
  beforeUpload: (file: File) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
    }
    return isImage || Upload.LIST_IGNORE;
  },
};

// Antd form integration
UploadUtils.formItemProps = {
  valuePropName: "fileList",
  getValueFromEvent: (e: any) => {
    return Array.isArray(e) ? e : e && e.fileList;
  },
};

// Helpers to transform upload results
UploadUtils.processUploadImage = (
  x: any,
  is_multiple: boolean = false
): any[] | any => {
  if (is_multiple) {
    return Array.isArray(x) ? x.map((y) => y?.response?.[0] ?? y) : [];
  }

  if (!["", null, undefined].includes(x?.[0]?.response)) {
    return x[0].response;
  }

  return x;
};

UploadUtils.processWithIDUploadImage = (
  x: any,
  is_multiple: boolean = false
): string | string[] | any => {
  if (is_multiple) {
    return Array.isArray(x)
      ? x.map((y) => y?.response?.[0]?.uid ?? y?.uid ?? y)
      : [];
  }

  if (!["", null, undefined].includes(x?.[0]?.response)) {
    return x[0].response?.[0]?.uid;
  }

  if (!["", null, undefined].includes(x?.[0]?.uid)) {
    return x[0]?.uid;
  }

  return x;
};

UploadUtils.processUploadMediaPicker = (files: any[]): string[] => {
  return files.map((file) => file.uid);
};

export default UploadUtils;
